<template lang="pug">
.service-page
  button.progress-quick-entry(type="button" @click="goServiceProgress" aria-label="进入我的照护")
    span.quick-entry-icon ◉
    span 我的照护
  .content
    .intro-page
      // 头部标题区
      .intro-header
        .header-logo
          img.logo(src="@/assets/logo.png")
          span.logo-text 袋鼠照护
        .header-title 石家庄早产儿家庭照护服务包
        .header-subtitle 医学证据支持 · 专业团队跟进 · 个性化照护计划
        .header-pre-slogan 早产宝宝的发育风险，往往不是现在出现
        .header-slogan 把握最关键的前 6 个月，走稳、走对

      // 第一部分:为什么需要专业照护
      .section.section-why
        .section-title
          .title-number 一
          span.title-text 早产宝宝看着好，是真的好吗？提防以下问题。
          span.title-tag 必须看
        .section-content
          .research-note
            span.research-source 国际顶级医学期刊JAMA《美国医学会杂志》 Network Open 2024 论文（Neurodevelopment at Age 9 Years Among Children Born at 32 to 36 Weeks’ Gestation）对 32–36 周早产儿进行了 9 年随访，结果非常明确：
          
          .highlight-points
            .point-item
              .point-icon ✦
              .point-content
                span.point-title 可能会出现发育不会自然追上
                span.point-desc 在 语言、数学、学习能力、注意力、行为控制、精细运动等多个领域，中晚期早产儿在 9 岁时仍显著落后足月儿，这些差距，往往在婴儿期并不明显。
            
            .point-item
              .point-icon ✦
              .point-content
                span.point-title 2 岁前的轻微落后，会影响 8、9 岁后的学业与行为问题
                span.point-desc 婴儿期看似"稍慢一点"，但如果缺乏科学及时干预，会在学龄阶段被放大。
            
            .point-item
              .point-icon ✦
              .point-content
                span.point-title 既然 32–36 周都有风险,那 <32 周的宝宝更需要专业照护
                span.point-desc 医学研究长期证实：胎龄越小，大脑越不成熟，风险越高。32 周以下(包括极早、超早)更是典型的需要长期随访与家庭干预的人群。

      // 第二部分:为什么家长很难做到
      .section.section-challenge
        .section-title
          .title-number 二
          span.title-text 为什么家长自己很难做到科学照护?
        .section-content
          .challenge-list
            .challenge-item
              .challenge-number ①
              .challenge-content
                span.challenge-title 家长不知道"什么时候该做什么做到什么程度"
                span.challenge-desc 喂养、神经发育训练、睡眠、环境布置、体重增速、反流、胀气……每个环节都与足月儿不同，每个早产儿也都不一样。靠自己查资料，容易用错方法。
            
            .challenge-item
              .challenge-number ②
              .challenge-content
                span.challenge-title 很多发育偏差外表完全看不出来
                span.challenge-desc 注意力、执行功能、精细动作、语言发展差异往往要到 18 个月–9 岁才显现。婴儿期靠家长肉眼很难识别。
            
            .challenge-item
              .challenge-number ③
              .challenge-content
                span.challenge-title 靠"几个月一次的随访"远远不够
                span.challenge-desc 发育问题是积累性的，需要每周甚至每日监测与调整，而不是"等下次门诊再看"。
            
            .challenge-item
              .challenge-number ④
              .challenge-content
                span.challenge-title 家长描述 ≠ 宝宝真实状态
                span.challenge-desc 上门数据反复证明：家长对"吃奶量、动作质量、训练合适度、睡眠环境"等的判断常常与实际不一致，而这些差异会影响孩子的追赶速度。
          
          .conclusion-box
            span.conclusion-text 早产儿照护是一个"专业决策过程"，不是靠爱和努力就能做好的事。

      // 第三部分:我们能为你做什么
      .section.section-service
        .section-title
          .title-number 三
          span.title-text 袋鼠照护服务包能为你解决什么问题?
        .section-content
          .service-cards
            // 服务卡片1
            .service-card
              .card-header
                span.card-number 1)
                span.card-title 发育窗口期上门指导训练：同时盯住体格发育和神经发育
              .card-body
                span.card-intro 家长描述的情况，和宝宝真实状态一定一样吗？宝宝到了这个阶段该怎么长、该预防什么，照护人真的清楚吗？
                .card-list
                  span.list-item • 体格发育：现场看体重、身长、头围、奶量、吞咽、喂养姿势和睡眠状态，判断追赶节奏是否合适
                  span.list-item • 体格发育：结合居家记录，告诉家长这个阶段重点盯什么、哪些情况需要提前预防
                  span.list-item • 神经发育：现场看张力、姿势、大运动、精细运动、视觉追踪和行为反应，识别早期偏差
                  span.list-item • 神经发育：在宝宝发育窗口期，上门指导家庭训练动作，纠正照护人做不到位的地方
                span.card-highlight 很多问题不是家长不认真，而是没有专业视角看不出来。上门的价值，是把“感觉还好”变成“知道该怎么做”。

            // 服务卡片2
            .service-card
              .card-header
                span.card-number 2)
                span.card-title 每周电话回访：主动发现问题，而不是等小问题积累成大问题，早产儿的问题，发现早 = 调整成本低
              .card-body
                span.card-intro 我们会每周询问：
                .card-list
                  span.list-item • 奶量是否需要调整?
                  span.list-item • 睡眠是否规律?
                  span.list-item • 大便是否异常?
                  span.list-item • 当前训练是否适合?
                  span.list-item • 有无反流、胀气、过度兴奋?
                  span.list-item • 家长焦虑程度是否增加?
                span.card-highlight 所有需要纠正的地方，我们会主动发现，不让小问题累积成大问题。

            // 服务卡片3
            .service-card
              .card-header
                span.card-number 3)
                span.card-title 24 小时线上答疑：时间敏感的问题不过夜，早产儿的紧急情况，不能等
              .card-body
                span.card-intro 早产儿的问题，不等天亮。我们提供：
                .card-list
                  span.list-item • 夜间紧急情况快速响应
                  span.list-item • 训练/喂养即时指导
                  span.list-item • 反流、异常哭闹、吃奶突降的处理方案
                  span.list-item • 专家支持升级入口
                span.card-highlight 付费用户享有优先通道，不会再"等照护师什么时候有空"。

            // 服务卡片4
            .service-card
              .card-header
                span.card-number 4)
                span.card-title AI + 专家团队：为宝宝生成"个性化照护路径"，避免家长"盲目试错"
              .card-body
                span.card-intro 我们的 AI 系统整合：
                .card-list
                  span.list-item • 医嘱
                  span.list-item • 上门评估数据
                  span.list-item • 每周回访
                  span.list-item • 宝宝每日体重/吃奶/睡眠数据
                  span.list-item • 家长上传视频
                  span.list-item • 随访复查报告
                span.card-summary 自动分析宝宝的发育状态，由照护团队审核后生成：
                span.card-summary 宝宝下一阶段要做的训练、喂养计划、安全注意事项、监测指标。
                span.card-highlight 家长只需按计划执行 —— 不用自己查、不用猜、不犯错，让宝宝的成长更有确定性，也让家庭不被长期的不确定性消耗。

      // 第四部分:科学依据
      .section.section-evidence
        .section-title
          .title-number 四
          span.title-text 来源于顶刊的科学依据
        .section-content
          .evidence-list
            .evidence-item(@click="openLink('https://jamanetwork.com/journals/jamanetworkopen/fullarticle/2826283')")
              span.evidence-text • JAMA Network Open 2024（《美国医学会杂志》）
              span.evidence-icon →
            .evidence-item(@click="openLink('https://publications.aap.org/pediatrics/article/152/6/e2023061878/195044/Risk-of-Developmental-Disorders-in-Children-Born')")
              span.evidence-text • JAMA Pediatrics（美国儿科学会《儿科学》）
              span.evidence-icon →
            .evidence-item(@click="openLink('https://pmc.ncbi.nlm.nih.gov/articles/PMC7082240/')")
              span.evidence-text • Translational Pediatrics （《转化儿科学杂志》）
              span.evidence-icon →
            .evidence-item(@click="openLink('https://pubmed.ncbi.nlm.nih.gov/38267070/')")
              span.evidence-text • The BMJ （《英国医学杂志》）
              span.evidence-icon →
            .evidence-item(@click="openLink('https://pubmed.ncbi.nlm.nih.gov/27479919/')")
              span.evidence-text • JAMA Pediatrics（美国儿科学会《儿科学》）
              span.evidence-icon →

      // 第五部分:最终获得
      .section.section-result
        .section-title
          .title-number 五
          span.title-text 最终获得
        .section-content
          .result-list
            .result-item
              span.result-check ✔
              span.result-text 宝宝更快追赶体格与能力
            .result-item
              span.result-check ✔
              span.result-text 早期发现问题，避免错过干预窗口
            .result-item
              span.result-check ✔
              span.result-text 家长省心，不再到处查资料，简单照做即可
            .result-item
              span.result-check ✔
              span.result-text 减少不必要就医与家庭焦虑
            .result-item
              span.result-check ✔
              span.result-text 更稳定的家庭节奏与育儿信心
          
          .final-message
            span.final-text 这不是简单的上门和线上答疑，而是给孩子一套"正确成长的系统"。

      // 智能体重秤
      .section.section-scale
        .section-title
          span.title-text 智能体重秤：宝宝回家后的体重变化，家里也能持续记录
        .section-content
          .scale-showcase
            .scale-copy
              span.scale-eyebrow 服务包内包含
              span.scale-desc 早产宝宝不是只看“有没有长”，更要看增长速度是否合适。家里能稳定记录体重，照护团队才更容易判断喂养、睡眠和发育节奏要不要调整。
            .scale-photo-grid
              img.scale-photo.scale-photo-main(src="/pay2/smart-scale-1.jpg" alt="袋鼠照护智能体重秤")
              img.scale-photo(src="/pay2/smart-scale-2.jpg" alt="智能体重秤连接记录")
              img.scale-photo(src="/pay2/smart-scale-3.jpg" alt="智能体重秤测量界面")
              img.scale-photo(src="/pay2/smart-scale-4.jpg" alt="智能体重秤细节")
            .scale-points
              .scale-point
                span.point-name 在家测
                span.point-text 不用等到下次门诊，先看到宝宝这几天的体重变化。
              .scale-point
                span.point-name 有曲线
                span.point-text 每次记录都能沉淀下来，判断追赶节奏有没有偏慢或波动。
              .scale-point
                span.point-name 能联动
                span.point-text 照护师结合体重、喂养记录、电话回访和上门评估一起看，不让家长只靠印象判断。
            span.scale-highlight 秤本身不是重点，重点是这些记录会进入照护判断，照护师会结合宝宝情况告诉你下一步该怎么做。

      // 风险 vs 保障对比表
      .section.section-comparison
        .section-title
          span.title-text 风险 vs 保障对比表
        .section-content
          .comparison-table
            .table-header
              span.header-item.header-item-main 情况
              span.header-item 不升级
              span.header-item.header-item-highlight 升级服务
            .table-row
              span.row-label 发育节奏是否有人盯
              span.row-value ❌
              span.row-value.row-value-highlight ✔
            .table-row
              span.row-label 偏差是否早发现
              span.row-value ❌
              span.row-value.row-value-highlight ✔
            .table-row
              span.row-label 是否动态调整计划
              span.row-value ❌
              span.row-value.row-value-highlight ✔
            .table-row
              span.row-label 是否反复试错
              span.row-value 可能性高
              span.row-value.row-value-highlight 低
            .table-row
              span.row-label 风险是否更可控
              span.row-value ❌
              span.row-value.row-value-highlight ✔
          
          .comparison-note
            span.note-text 这是一次用相对低的成本，换取宝宝关键阶段"更高确定性"的选择。

      // 服务包价格标题
      .package-compare-title
        span.pc-title 石家庄付费服务怎么选?

      // 科研组 / 石家庄基础守护版 / 石家庄成长增强版对比区块
      .package-compare
        .compare-inner
          .compare-header
            span.compare-title 服务项
            span.compare-col 科研组基础随访
            span.compare-col 基础守护版
            span.compare-col.vip 成长增强版

          .compare-row
            span.row-key 服务周期
            span.row-val 项目基础随访
            .row-val
              div 90天重点照护
              div +3个月基础陪伴
            .row-val
              div 180天个性化
              div +6个月基础陪伴

          .compare-row
            span.row-key 上门访视
            span.row-val 送首次
            span.row-val 送首次 + 3次
            span.row-val 送首次 + 5次

          .compare-row
            span.row-key 图文咨询
            span.row-val 工作时服务
            span.row-val 90天不限次
            span.row-val 8:00-21:00 + 紧急通道

          .compare-row
            span.row-key 电话咨询
            span.row-val -
            span.row-val 4-6次
            span.row-val 前3个月每周，后续每月

          .compare-row
            span.row-key 视频指导
            span.row-val —
            span.row-val 3次
            span.row-val 6次

          .compare-row
            span.row-key 喂养调整
            span.row-val 基础提醒
            span.row-val 个性化调整
            span.row-val 深度跟踪

          .compare-row
            .row-key
              div 神经发育
              div 家庭促进
            span.row-val —
            span.row-val 3个月
            span.row-val 3-6个月

          .compare-row
            span.row-key 门诊陪诊
            span.row-val —
            span.row-val 包含
            span.row-val 包含

          .compare-row
            span.row-key 电话回访
            span.row-val -
            span.row-val 双周为主
            span.row-val 前3个月每周1次

          .compare-row
            span.row-key 随访提醒
            span.row-val 基础提醒
            span.row-val 包含
            span.row-val 包含

          .compare-row
            span.row-key 辅食指导
            span.row-val 随访时指导
            span.row-val 服务内指导
            span.row-val 个性化辅食清单及指导

          .compare-row
            span.row-key 隔代养育冲突
            span.row-val —
            span.row-val 包含
            span.row-val 包含

          .compare-row
            span.row-key 婴儿体重秤
            span.row-val —
            span.row-val 包含
            span.row-val 包含

          .compare-row.compare-price
            span.row-key 价格
            span.row-val 免费
            .row-val
              div.price-current 2280元
              div.price-original 原价2580元
            .row-val
              div.price-current 3280元
              div.price-original 原价3580元

      .service-progress-entry(@click="goServiceProgress")
        .entry-mark
          span.entry-mark-line
          span.entry-mark-dot
        .entry-copy
          span.entry-eyebrow 购买后可见
          span.entry-title 我的照护 · 服务进展
          span.entry-desc 每一项服务、每一次宝宝变化，都清楚看得见。
        span.entry-action
          span 查看示意
          span.entry-arrow →

      // 第六部分:退款说明
      .section.section-refund
        .section-title
          .title-number 六
          span.title-text 退款说明
        .section-content
          .refund-principle
            span.principle-title 随时退款原则
            span.principle-desc 袋鼠照护服务包支持在服务周期内按实际使用情况退款。服务到期后，未使用的正式上门次数不过期，仍可继续预约；线上咨询恢复为科研组基础咨询，不再享受付费包 7×24 小时紧急优先回复。

          .refund-details
            span.details-title 服务计费说明：
            
            .refund-item
              .item-header
                span.item-number 1
                span.item-title 线上图文服务
              span.item-desc 按已服务天数计算。电话咨询和视频指导属于服务周期内的照护权益，不单独拆分计算退款。

            .refund-item
              .item-header
                span.item-number 2
                span.item-title 上门服务
              span.item-desc 按正式上门次数消耗计算。送首次用于入户评估和建立照护关系，不计入正式上门次数，也不单独折算退款。

            .refund-item
              .item-header
                span.item-number 3
                span.item-title 智能体重秤
              span.item-desc 体重秤是服务包包含内容，购买服务后默认包含。如家里已有体重秤、不需要配置，可提前与赵护士沟通，按体重秤成本减免费用：
              .item-sub
                span.sub-item • 体重秤费用按实际采购成本减免或扣除

            .refund-item
              .item-header
                span.item-number 4
                span.item-title 服务到期后的使用
              span.item-desc 服务周期结束后，未用完的正式上门次数可以继续预约；但电话、视频和7×24小时线上紧急优先回复随服务周期结束而停止。

          .refund-example
            span.example-title 💡 退款计算示例：
              span.example-desc 购买3280元石家庄成长增强版，使用了30天，完成2次正式上门服务，体重秤已使用：
            .example-calc
              span.calc-line 3280元(支付金额)
              span.calc-line - 125元(智能体重秤)
              span.calc-line - 654元(30天线上 × 21.8元)
              span.calc-line - 874元(2次上门 × 437元)
              span.calc-result = 1627元(退款金额)
</template>

<script>
export default {
    data () {
        return {
            regions: []
        };
    },
    mounted () {
        this.fetchPurchasedInfo();
    },
    methods: {
    // 拉取购买信息
        fetchPurchasedInfo () {
            // 这里可以根据需要添加 API 调用
            // 示例数据
            this.regions = [
                { city: '北京', name: '张**' },
                { city: '上海', name: '王**' },
                { city: '深圳', name: '李**' }
            ];
        },
        // 领取优惠券
        goToCoupon () {
            this.$router.push('/pay2/coupon');
        },
        goServiceProgress () {
            this.$router.push({
                path: '/service/progress',
                query: {
                    ...this.$route.query
                }
            });
        },
        // 打开外部链接
        openLink (url) {
            window.open(url, '_blank');
        }
    }
};
</script>

<style lang="scss" scoped>
/* 新版介绍页样式 */
.service-page {
  background: #f8f8f8;
  position: relative;
  min-height: 100vh;

  .content {
    padding-bottom: 40px;
  }

  .progress-quick-entry {
    position: fixed;
    right: 16px;
    bottom: calc(20px + env(safe-area-inset-bottom));
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 13px;
    border: 1px solid #9AC5B1;
    border-radius: 19px;
    background: #F7FCF8;
    box-shadow: 0 7px 18px rgba(31, 90, 67, 0.16);
    color: #157866;
    font-size: 13px;
    font-weight: 600;
    line-height: 38px;
    cursor: pointer;

    .quick-entry-icon {
      color: #D96A4C;
      font-size: 16px;
      line-height: 1;
    }
  }

  .intro-page {
    background: #fff;
    
    // 头部标题区
    .intro-header {
      background: linear-gradient(135deg, #05C2CB 0%, #0EA5AE 100%);
      padding: 32px 18px;
      text-align: center;
      color: #fff;
      
      .header-logo {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
        
        .logo {
          width: 32px;
          height: 32px;
          margin-right: 8px;
        }
        
        .logo-text {
          font-size: 16px;
          font-weight: 600;
        }
      }
      
      .header-title {
        font-size: 22px;
        font-weight: 700;
        margin-bottom: 12px;
        line-height: 1.4;
      }
      
      .header-subtitle {
        font-size: 16px;
        font-weight: 500;
        opacity: 0.95;
        margin-bottom: 8px;
      }
      
      .header-pre-slogan {
        font-size: 14px;
        font-weight: 400;
        opacity: 0.9;
        margin-bottom: 12px;
        margin-top: 16px;
      }
      
      .header-slogan {
        font-size: 18px;
        font-weight: 600;
        background: rgba(255, 255, 255, 0.2);
        padding: 8px 16px;
        border-radius: 20px;
        display: inline-block;
        margin-top: 8px;
      }
    }
    
    // 通用区块样式
    .section {
      padding: 24px 18px;
      border-bottom: 8px solid #f8f8f8;
      
      .section-title {
        display: flex;
        align-items: center;
        margin-bottom: 18px;
        
        .title-number {
          width: 32px;
          height: 32px;
          background: #05C2CB;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          margin-right: 12px;
          flex-shrink: 0;
        }
        
        .title-text {
          font-size: 18px;
          font-weight: 700;
          color: #000;
          flex: 1;
        }
        
        .title-tag {
          background: #FF6B6B;
          color: #fff;
          font-size: 12px;
          padding: 4px 12px;
          border-radius: 12px;
          margin-left: 8px;
        }
      }
      
      .section-content {
        font-size: 14px;
        line-height: 1.8;
        color: #333;
      }
    }
    
    // 第一部分样式
    .section-why {
      .research-note {
        background: #FFF9E6;
        padding: 16px;
        border-radius: 8px;
        margin-bottom: 18px;
        border-left: 4px solid #FFC107;
        
        .research-source {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
        }
      }
      
      .highlight-points {
        .point-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 18px;
          padding: 16px;
          background: #F6F7F9;
          border-radius: 8px;
          
          .point-icon {
            color: #05C2CB;
            font-size: 18px;
            margin-right: 12px;
            flex-shrink: 0;
            margin-top: 2px;
          }
          
          .point-content {
            flex: 1;
            
            .point-title {
              display: block;
              font-size: 15px;
              font-weight: 600;
              color: #000;
              margin-bottom: 8px;
            }
            
            .point-desc {
              display: block;
              font-size: 14px;
              color: #666;
              line-height: 1.6;
            }
          }
        }
      }
    }
    
    // 第二部分样式
    .section-challenge {
      .challenge-list {
        .challenge-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 18px;
          
          .challenge-number {
            width: 28px;
            height: 28px;
            background: #E8F7F8;
            color: #05C2CB;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 600;
            margin-right: 12px;
            flex-shrink: 0;
          }
          
          .challenge-content {
            flex: 1;
            
            .challenge-title {
              display: block;
              font-size: 15px;
              font-weight: 600;
              color: #000;
              margin-bottom: 6px;
            }
            
            .challenge-desc {
              display: block;
              font-size: 14px;
              color: #666;
              line-height: 1.6;
            }
          }
        }
      }
      
      .conclusion-box {
        background: linear-gradient(135deg, #05C2CB 0%, #0EA5AE 100%);
        padding: 18px;
        border-radius: 8px;
        margin-top: 18px;
        text-align: center;
        
        .conclusion-text {
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          line-height: 1.6;
        }
      }
    }
    
    // 第三部分样式
    .section-service {
      .service-cards {
        .service-card {
          background: #F6F7F9;
          border-radius: 12px;
          padding: 18px;
          margin-bottom: 16px;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          .card-header {
            display: flex;
            align-items: flex-start;
            margin-bottom: 12px;
            
            .card-number {
              color: #05C2CB;
              font-size: 16px;
              font-weight: 700;
              margin-right: 8px;
              flex-shrink: 0;
            }
            
            .card-title {
              font-size: 16px;
              font-weight: 600;
              color: #000;
              line-height: 1.4;
              flex: 1;
            }
          }
          
          .card-body {
            padding-left: 24px;
            
            .card-intro {
              display: block;
              font-size: 14px;
              color: #666;
              margin-bottom: 12px;
            }
            
            .card-list {
              .list-item {
                display: block;
                font-size: 14px;
                color: #666;
                line-height: 1.8;
                padding-left: 8px;
              }
            }
            
            .card-summary {
              display: block;
              font-size: 14px;
              color: #666;
              line-height: 1.6;
              margin-top: 12px;
            }
            
            .card-highlight {
              display: block;
              background: #fff;
              padding: 12px;
              border-radius: 6px;
              margin-top: 12px;
              font-size: 14px;
              color: #05C2CB;
              font-weight: 500;
              line-height: 1.6;
              border-left: 3px solid #05C2CB;
            }
          }
        }
      }
    }
    
    // 第四部分样式
    .section-evidence {
      .evidence-list {
        .evidence-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px;
          background: #F6F7F9;
          border-radius: 8px;
          margin-bottom: 10px;
          cursor: pointer;
          
          &:active {
            background: #E8F7F8;
          }
          
          .evidence-text {
            font-size: 14px;
            color: #000;
            flex: 1;
          }
          
          .evidence-icon {
            color: #05C2CB;
            font-size: 18px;
            margin-left: 12px;
          }
        }
      }
    }
    
    // 第五部分样式
    .section-result {
      .result-list {
        .result-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 14px;
          padding: 14px;
          background: #E8F7F8;
          border-radius: 8px;
          
          .result-check {
            color: #05C2CB;
            font-size: 18px;
            margin-right: 12px;
            flex-shrink: 0;
          }
          
          .result-text {
            font-size: 15px;
            color: #000;
            line-height: 1.6;
            flex: 1;
          }
        }
      }
      
      .final-message {
        background: linear-gradient(135deg, #05C2CB 0%, #0EA5AE 100%);
        padding: 20px;
        border-radius: 12px;
        margin-top: 20px;
        text-align: center;
        
        .final-text {
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          line-height: 1.6;
        }
      }
    }

    // 智能体重秤
    .section-scale {
      .scale-showcase {
        background: #F6F7F9;
        border: 1px solid #E6EEF0;
        border-radius: 8px;
        padding: 16px;
      }

      .scale-copy {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 14px;
      }

      .scale-eyebrow {
        width: fit-content;
        padding: 4px 8px;
        background: #E8F7F8;
        border-radius: 4px;
        color: #05AEB7;
        font-size: 12px;
        font-weight: 700;
      }

      .scale-title {
        color: #000;
        font-size: 17px;
        font-weight: 700;
        line-height: 1.45;
      }

      .scale-desc {
        color: #666;
        font-size: 14px;
        line-height: 1.7;
      }

      .scale-photo-grid {
        display: grid;
        grid-template-columns: 1.3fr 1fr;
        grid-template-rows: repeat(3, 76px);
        gap: 8px;
        margin-bottom: 14px;
      }

      .scale-photo {
        width: 100%;
        height: 100%;
        border-radius: 6px;
        object-fit: cover;
        background: #fff;
      }

      .scale-photo-main {
        grid-row: 1 / span 3;
      }

      .scale-points {
        display: grid;
        gap: 8px;
        margin-bottom: 12px;
      }

      .scale-point {
        display: grid;
        grid-template-columns: 58px 1fr;
        column-gap: 10px;
        align-items: flex-start;
        padding: 10px;
        background: #fff;
        border-radius: 6px;
      }

      .point-name {
        color: #05AEB7;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.5;
      }

      .point-text {
        color: #555;
        font-size: 13px;
        line-height: 1.6;
      }

      .scale-highlight {
        display: block;
        padding: 12px;
        background: #FFF9E6;
        border-left: 3px solid #FFC107;
        border-radius: 6px;
        color: #6B5A1F;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.6;
      }
    }

    // 风险 vs 保障对比表样式
    .section-comparison {
      .comparison-table {
        background: #fff;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #E6EEF0;
        
        .table-header {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr;
          background: linear-gradient(90deg, #F6FBFB 0%, #FFFFFF 100%);
          border-bottom: 2px solid #05C2CB;
          
          .header-item {
            padding: 14px 12px;
            font-size: 14px;
            font-weight: 700;
            color: #333;
            text-align: center;
            
            &.header-item-main {
              text-align: left;
            }
            
            &.header-item-highlight {
              color: #05C2CB;
            }
          }
        }
        
        .table-row {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr;
          border-bottom: 1px solid #EEF6F6;
          
          &:last-child {
            border-bottom: none;
          }
          
          .row-label {
            padding: 14px 12px;
            font-size: 14px;
            color: #333;
            font-weight: 500;
          }
          
          .row-value {
            padding: 14px 12px;
            font-size: 16px;
            text-align: center;
            color: #000;
            
            &.row-value-highlight {
              color: #05C2CB;
              font-weight: 600;
            }
          }
        }
      }
      
      .comparison-note {
        background: #FFF9E6;
        padding: 14px 16px;
        border-radius: 8px;
        margin-top: 16px;
        text-align: center;
        border-left: 3px solid #FFC107;
        
        .note-text {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
        }
      }
    }

    // 优惠券入口
    .coupon-entry {
      margin: 16px 16px 0;
      padding: 16px;
      background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-shadow: 0 4px 12px rgba(255, 107, 107, 0.2);
      position: relative;
      overflow: hidden;
      cursor: pointer;

      &::before {
        content: '';
        position: absolute;
        right: -20px;
        top: -20px;
        width: 100px;
        height: 100px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
      }

      .coupon-left {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        z-index: 1;

        .coupon-icon {
          width: 44px;
          height: 44px;
          background: rgba(255, 255, 255, 0.25);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);

          .icon-tag {
            font-size: 20px;
            font-weight: bold;
            color: #fff;
          }
        }

        .coupon-info {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .coupon-title {
            font-size: 16px;
            font-weight: 600;
            color: #fff;
            line-height: 1.2;
          }

          .coupon-desc {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.9);
            line-height: 1.2;
          }
        }
      }

      .coupon-right {
        display: flex;
        align-items: center;
        gap: 4px;
        z-index: 1;

        .coupon-action {
          font-size: 14px;
          font-weight: 500;
          color: #fff;
        }

        .arrow {
          font-size: 24px;
          color: #fff;
          font-weight: 300;
        }
      }

      &:active {
        opacity: 0.9;
        transform: scale(0.98);
      }
    }

    /* 服务包价格标题 */
    .package-compare-title {
      margin: 12px 16px 6px;
      padding-left: 6px;
      
      .pc-title {
        font-size: 16px;
        font-weight: 700;
        color: #333;
      }
    }

    /* 基础版 / 尊享半年包对比样式（使用 grid 对齐列） */
    .package-compare {
      margin: 12px 16px 8px;
      border-radius: 8px;
      background: #fff;
      border: 1px solid #E6EEF0;
      /* scroll-view 需要设置宽度，通常是 auto 或 100% */
      width: auto;
      overflow-x: auto; /* 允许横向滚动 */

      .compare-inner {
        min-width: 620px; /* 石家庄版三档对比，移动端横向滚动 */
        padding-bottom: 2px;
      }

      .compare-header,
      .compare-row {
        display: grid;
        /* 固定三列比例，配合 min-width: 520px 实现横向滚动 */
        grid-template-columns: 92px 1fr 1.15fr 1.15fr;
        align-items: center;
        padding: 12px 10px;
        border-bottom: 1px solid #EEF6F6;
        column-gap: 10px;
      }

      .compare-header {
        background: linear-gradient(90deg, #F6FBFB 0%, #FFFFFF 100%);
        font-weight: 700;
      }

      .compare-title {
        color: #666;
        font-size: 13px;
      }

      .compare-col {
        text-align: left;
        color: #333;
        font-size: 13px;
      }

      .compare-col.vip {
        color: #07C160;
      }

      .row-key {
        color: #666;
        font-size: 13px;
        white-space: normal;
        line-height: 1.4;
      }

      .row-val {
        text-align: left;
        color: #333;
        font-size: 13px;
        white-space: normal;
        line-height: 1.4;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .compare-price .row-val {
        font-weight: 700;
        color: #05C2CB;

        .price-current {
          color: #05C2CB;
          font-size: 15px;
          line-height: 1.3;
        }

        .price-original {
          color: #999;
          font-size: 12px;
          font-weight: 400;
          text-decoration: line-through;
          line-height: 1.3;
        }
      }
    }

    // 第六部分:退款说明样式
    .section-refund {
      .refund-principle {
        background: #FFF9E6;
        padding: 16px;
        border-radius: 8px;
        margin-bottom: 18px;
        border-left: 4px solid #FFC107;

        .principle-title {
          display: block;
          font-size: 16px;
          font-weight: 700;
          color: #000;
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .principle-desc {
          display: block;
          font-size: 14px;
          color: #666;
          line-height: 1.6;
        }
      }

      .refund-details {
        .details-title {
          display: block;
          font-size: 15px;
          font-weight: 600;
          color: #000;
          margin-bottom: 16px;
        }

        .refund-item {
          background: #F6F7F9;
          border-radius: 8px;
          padding: 14px;
          margin-bottom: 12px;

          .item-header {
            display: flex;
            align-items: center;
            margin-bottom: 8px;

            .item-number {
              width: 24px;
              height: 24px;
              background: #05C2CB;
              color: #fff;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 13px;
              font-weight: 600;
              margin-right: 10px;
              flex-shrink: 0;
            }

            .item-title {
              font-size: 15px;
              font-weight: 600;
              color: #000;
            }
          }

          .item-desc {
            display: block;
            font-size: 14px;
            color: #666;
            line-height: 1.6;
            padding-left: 34px;
          }

          .item-sub {
            padding-left: 34px;
            margin-top: 8px;

            .sub-item {
              display: block;
              font-size: 13px;
              color: #666;
              line-height: 1.8;
            }
          }
        }
      }

      .refund-example {
        background: linear-gradient(135deg, #E8F7F8 0%, #F0FAFB 100%);
        border-radius: 12px;
        padding: 16px;
        margin-top: 18px;

        .example-title {
          display: block;
          font-size: 15px;
          font-weight: 700;
          color: #05C2CB;
          margin-bottom: 12px;
        }

        .example-desc {
          display: block;
          font-size: 14px;
          color: #333;
          line-height: 1.6;
          margin-bottom: 14px;
        }

        .example-calc {
          background: #fff;
          border-radius: 8px;
          padding: 14px;
          border-left: 3px solid #05C2CB;

          .calc-line {
            display: block;
            font-size: 13px;
            color: #666;
            line-height: 1.8;
            font-family: 'Monaco', 'Courier New', monospace;
          }

          .calc-result {
            display: block;
            font-size: 15px;
            color: #05C2CB;
            font-weight: 700;
            line-height: 1.8;
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px dashed #E6E6E6;
            font-family: 'Monaco', 'Courier New', monospace;
          }
        }
      }
    }

    .service-progress-entry {
      display: flex;
      align-items: center;
      gap: 13px;
      margin: 18px 16px 26px;
      padding: 16px;
      border: 1px solid #B8D5C7;
      border-radius: 8px;
      background: #F5FAF7;
      box-shadow: 0 8px 18px rgba(39, 92, 68, 0.06);
      cursor: pointer;

      .entry-mark {
        position: relative;
        width: 30px;
        height: 42px;
        flex: 0 0 30px;

        .entry-mark-line {
          position: absolute;
          left: 14px;
          top: 3px;
          width: 2px;
          height: 36px;
          background: #8FBBA7;
        }

        .entry-mark-dot {
          position: absolute;
          left: 8px;
          top: 13px;
          width: 14px;
          height: 14px;
          border: 3px solid #F5FAF7;
          border-radius: 50%;
          background: #157866;
          box-shadow: 0 0 0 1px #157866;
        }
      }

      .entry-copy {
        min-width: 0;
        display: flex;
        flex: 1;
        flex-direction: column;
      }

      .entry-eyebrow {
        margin-bottom: 3px;
        color: #157866;
        font-size: 10px;
        font-weight: 600;
      }

      .entry-title {
        color: #203733;
        font-size: 16px;
        font-weight: 700;
      }

      .entry-desc {
        margin-top: 4px;
        color: #6D7D75;
        font-size: 11px;
        line-height: 1.5;
      }

      .entry-action {
        display: flex;
        align-items: center;
        gap: 5px;
        color: #157866;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
      }

      .entry-arrow {
        font-size: 17px;
        font-weight: 400;
      }
    }
  }
}
</style>
